import React, { useEffect, useState } from "react";
import { Clipboard, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import copyElementAsImage from "../../utils/copyElementAsImage";

/**
 * Small "copy as image" button for League Cup cards. Captures the element
 * behind `targetRef` as a PNG. The chip hides itself during capture.
 *
 * `variant`: "light" for light panels, "dark" for the champion tile /
 * group-card headers.
 */
const CopyImageChip = ({
    targetRef,
    fileName,
    label = "Copy as image",
    variant = "light",
}) => {
    const [state, setState] = useState("idle"); // idle | working | done

    useEffect(() => {
        if (state !== "done") return undefined;
        const timer = setTimeout(() => setState("idle"), 2500);
        return () => clearTimeout(timer);
    }, [state]);

    const handleClick = async () => {
        if (!targetRef.current || state === "working") return;
        setState("working");
        try {
            const result = await copyElementAsImage(targetRef.current, fileName);
            setState("done");
            if (result === "copied") toast.success("Copied image to clipboard!");
            else if (result === "downloaded") toast.success("Image downloaded.");
        } catch (err) {
            console.error("Copy as image failed:", err);
            setState("idle");
            toast.error("Couldn't copy the image.");
        }
    };

    const palette =
        variant === "dark"
            ? "text-white/80 bg-white/10 border-white/30 hover:bg-mustard hover:text-forest hover:border-mustard"
            : "text-text-mid bg-white border-border hover:bg-forest hover:text-white hover:border-forest";

    const Icon =
        state === "working" ? Loader2 : state === "done" ? Check : Clipboard;

    return (
        <button
            type="button"
            data-capture-hide
            onClick={handleClick}
            title="Copy as image"
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[10px] font-sans font-extrabold uppercase tracking-wide transition-colors ${palette}`}
        >
            <Icon className={`h-3 w-3 ${state === "working" ? "animate-spin" : ""}`} />
            {state === "done" ? "Copied!" : label}
        </button>
    );
};

export default CopyImageChip;
